import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { isAdmin } from '../../api/profile';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { fetchTelemetrySummary } from '../../features/telemetry';
import { DashboardPageHeader, DashboardDataTable } from '../../shared/ui';
import type { TelemetryPeriodBlock, TelemetrySummary } from '../../features/telemetry';

function mapApiError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return 'Some details are invalid. Please check and try again.';
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.status === 403) return 'Admin access is required.';
    return err.message || 'Something went wrong.';
  }
  return 'Something went wrong. Please try again.';
}

function formatChangePct(p: number | null): string {
  if (p === null) return '—';
  const sign = p >= 0 ? '+' : '';
  return `${sign}${p.toFixed(1)}%`;
}

function PeriodCard({
  title,
  subtitle,
  block,
}: {
  title: string;
  subtitle: string;
  block: TelemetryPeriodBlock;
}) {
  return (
    <div className="col-md-4">
      <div className="card h-100 shadow-sm">
        <div className="card-body">
          <h2 className="h6 text-muted mb-1">{title}</h2>
          <p className="display-6 fw-semibold mb-1">{block.uniqueVisitors}</p>
          <p className="small text-muted mb-2">{subtitle}</p>
          <p className="small mb-0">
            <span className="text-muted">vs prior period: </span>
            <span className="fw-semibold">{formatChangePct(block.changePercent)}</span>
            <span className="text-muted"> ({block.previousPeriodUniqueVisitors} visitors)</span>
          </p>
        </div>
      </div>
    </div>
  );
}

const TrafficAnalyticsPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [summary, setSummary] = useState<TelemetrySummary | null>(null);

  const load = useCallback(
    async (opts?: { notifySuccess?: boolean }) => {
      setLoading(true);
      setBanner(null);
      try {
        const data = await fetchTelemetrySummary();
        setSummary(data);
        if (opts?.notifySuccess) {
          showToast('Traffic data refreshed.');
        }
      } catch (err) {
        setBanner(mapApiError(err));
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  useEffect(() => {
    if (!isAdmin(profile)) return;
    void load();
  }, [load, profile]);

  if (!isAdmin(profile)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container-fluid px-4" style={{ maxWidth: 1100 }}>
      <DashboardPageHeader
        title="Traffic and analytics"
        description="First-party, anonymous visitors only (UTC). Signed-in users are excluded from public traffic counts. Paths show where visitors went on the public site."
        icon="bi bi-graph-up-arrow"
      >
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => void load({ notifySuccess: true })}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </DashboardPageHeader>

      {banner && (
        <div className="alert alert-danger" role="alert">
          {banner}
        </div>
      )}

      {summary && (
        <>
          <p className="small text-muted mb-3">Timezone for periods: {summary.timezone}</p>
          <div className="row g-3 mb-4">
            <PeriodCard
              title="Today (so far)"
              subtitle="Compared to full calendar yesterday (UTC)"
              block={summary.day}
            />
            <PeriodCard
              title="This week (so far)"
              subtitle="Monday start vs full previous week (UTC)"
              block={summary.week}
            />
            <PeriodCard
              title="This month (so far)"
              subtitle="vs full previous calendar month (UTC)"
              block={summary.month}
            />
          </div>

          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <span className="fw-semibold">Top paths (last 30 days)</span>
            </div>
            <div className="card-body p-0">
              {summary.topPathsLast30Days.length === 0 ? (
                <p className="text-muted mb-0 p-3">No path data yet.</p>
              ) : (
                <DashboardDataTable size="sm" aria-label="Top paths last 30 days">
                  <thead>
                    <tr>
                      <th>Path</th>
                      <th className="text-end">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                      {summary.topPathsLast30Days.map((row) => (
                        <tr key={row.path}>
                          <td className="font-monospace small">{row.path}</td>
                          <td className="text-end">{row.views}</td>
                        </tr>
                      ))}
                  </tbody>
                </DashboardDataTable>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TrafficAnalyticsPage;
